"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { withLocalePrefix } from "@/lib/i18nRouting";
import type { Locale } from "@/translations";
import { fetchCustomerHomeLoyaltyPoints } from "@/lib/customerHomeApi";

const LOYALTY_HASH = "#account-loyalty";

function loyaltyAccountHref(locale: Locale): string {
  return `${withLocalePrefix(locale, "/my-account")}${LOYALTY_HASH}`;
}

function guestLoginHref(locale: Locale): string {
  const ret = loyaltyAccountHref(locale);
  return withLocalePrefix(
    locale,
    `/login?returnUrl=${encodeURIComponent(ret)}`
  );
}

type Props = {
  className?: string;
  /** Narrower padding for sticky nav row */
  compact?: boolean;
};

const LOYALTY_COIN_SRC = "/images/loyalty-coin.png";

function LoyaltyCoinIcon({ compact }: { compact?: boolean }) {
  const px = compact ? 18 : 22;
  return (
    <img
      src={LOYALTY_COIN_SRC}
      alt=""
      width={px}
      height={px}
      className="shrink-0 object-contain"
      aria-hidden
    />
  );
}

export function NavLoyaltyPoints({ className = "", compact }: Props) {
  const { t, locale } = useLanguage();
  const { user, isReady } = useAuth();
  const [points, setPoints] = useState<number | null | undefined>(undefined);

  const canFetchPoints = !!(isReady && user?.customerId);
  const guestNav = isReady && !user;
  const linkHref = guestNav
    ? guestLoginHref(locale)
    : loyaltyAccountHref(locale);

  /** Avoid setState in an effect when fetch is disabled (guest / no id) — derive UI instead. */
  const displayPoints =
    !isReady ? undefined : !canFetchPoints ? null : points;

  useEffect(() => {
    if (!isReady || !canFetchPoints) return;
    let cancelled = false;
    (async () => {
      try {
        const id = user?.customerId?.trim();
        if (!id) {
          if (!cancelled) setPoints(null);
          return;
        }
        const n = await fetchCustomerHomeLoyaltyPoints(id);
        if (cancelled) return;
        if (typeof n === "number" && Number.isFinite(n)) {
          setPoints(n);
        } else {
          setPoints(null);
        }
      } catch {
        if (!cancelled) setPoints(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isReady, canFetchPoints, user?.customerId]);

  const pad = compact ? "px-0.5 py-2" : "px-3 py-2.5";
  const interactive =
    "cursor-pointer transition-colors hover:border-[#DD0428]/35 hover:bg-[#FFF0F0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#DD0428]";
  const base =
    `flex items-center rounded-lg border border-[#E5E5E5] bg-[#FFF8F8] ${pad} text-sm shrink-0 ${interactive} ${className}`.trim();

  if (displayPoints === undefined) {
    return (
      <Link
        href={linkHref}
        className={`${base} min-w-[5.5rem] justify-center gap-2`}
        title={t("nav.loyaltyPoints")}
        aria-label={t("nav.loyaltyPoints")}
      >
        <LoyaltyCoinIcon compact={compact} />
        <span className="h-4 w-12 animate-pulse rounded bg-[#E5E5E5]" aria-hidden />
      </Link>
    );
  }

  if (displayPoints === null) {
    return (
      <Link
        href={linkHref}
        className={`${base} gap-2 text-[#9CA3AF] tabular-nums`}
        title={t("nav.loyaltyPoints")}
        aria-label={t("nav.loyaltyPoints")}
      >
        <LoyaltyCoinIcon compact={compact} />
        —
      </Link>
    );
  }

  const formatted = new Intl.NumberFormat(
    locale === "ar" ? "ar-SA" : "en-US"
  ).format(displayPoints);

  return (
    <Link
      href={linkHref}
      className={`${base} gap-0.5 md:gap-2`}
      title={t("nav.loyaltyPoints")}
      aria-label={`${t("nav.loyaltyPoints")}: ${formatted} ${t("nav.pts")}`}
    >
      <LoyaltyCoinIcon compact={compact} />
      <span className="flex items-baseline gap-0.5 md:gap-1.5">
        <span className="md:font-bold font-medium tabular-nums text-[#BD2226]">{formatted}</span>
        <span className="text-xs md:font-medium font-normal text-[#6B7280]">
          {t("nav.pts")}
        </span>
      </span>
    </Link>
  );
}
